import pickBy from 'lodash/pickBy'

import { excludeAbortError } from '@app/errors/AbortError'

import { compact } from '@app/utils/compactV2Entity'
import { createSelector } from '@app/utils/createSelector'
import { normalizeSafe } from '@app/utils/normalizer'

import type { ApiAvatar, ApiSitter, ApiUser } from '@app/packages/api/v2/types/entities'

import { createReduxSlice } from '@app/store/redux_slice'
import type { FirebaseBatch } from '@app/store/types/firebase'
import type { StoreUser, UsersModelsState } from '@app/store/types/users'

const usersModelsSlice = createReduxSlice<UsersModelsState>('users_models')

/**
 * A read that returns whole users: what it says about avatars is the whole truth, so an absent
 * relationship clears them. Anything that merely mentions a user goes through
 * updateUsersFromResponseSlice instead, which keeps what we already hold.
 */
export const setUsersFromResponseSlice = (payload: unknown) => usersModelsSlice.update(state => foldWholeUsers(state, payload))

function foldWholeUsers(state: UsersModelsState | undefined, payload: unknown): UsersModelsState {
  const newState = { ...state }
  const { avatars: avatarsMap = {}, users = {} } = normalizeSafe<ApiUser | ApiAvatar>(payload)

  for (const rawUser of Object.values(users)) {
    const avatars = rawUser?.relationships?.avatars?.data?.map(m => compact(avatarsMap[m.id])) ?? []
    const avatar_url = avatars.at(0)?.image.url ?? null
    const user: StoreUser = {
      ...newState[rawUser.id],
      ...pickBy(compact(rawUser), (v, k) => ((k as keyof StoreUser) === 'phone' ? !!v : true)),
      ...(rawUser.attributes.account_type === 'sitter' ? { inaccurate_location_id: (rawUser as ApiSitter).relationships?.inaccurate_location?.data?.id } : {}),
      avatars,
      avatar_url,
      avatarsComplete: true,
    }

    newState[user.id] = user
    newState[user.token] = user
    if (user.chat_server_uid) newState[user.chat_server_uid] = user
  }

  return newState
}

/**
 * Any response can bring users along, so this folds them out of the payload rather than out of a
 * case handler per endpoint. Avatars are only replaced when the payload actually carries them —
 * a user mentioned in passing says nothing about the avatars we already hold.
 */
export const updateUsersFromResponseSlice = (payload: unknown) =>
  usersModelsSlice.update(state => {
    const { users, avatars = {} } = normalizeSafe<ApiUser | ApiAvatar>(payload)
    if (!users) return state

    const models = Object.values(users)
      .map((u): StoreUser => {
        const stateUser = (state ?? EMPTY_MODELS)[u.id]
        const stateHasAvatars = stateUser && stateUser.avatarsComplete
        const dataHasAvatars = !!u.relationships?.avatars?.data
        const userAvatars = u.relationships?.avatars?.data?.map(r => compact(avatars[r.id])) ?? []
        const avatar_url = userAvatars.at(0)?.image.url ?? null
        return {
          ...stateUser,
          ...pickBy(compact(u), (v, k) => ((k as keyof StoreUser) === 'phone' ? !!v : true)),
          ...(u.attributes.account_type === 'sitter' ? { inaccurate_location_id: (u as ApiSitter).relationships?.inaccurate_location?.data?.id } : {}),
          ...(dataHasAvatars ? { avatars: userAvatars, avatar_url } : { avatars: stateUser?.avatars ?? [], avatar_url: stateUser?.avatar_url ?? null }),
          avatarsComplete: stateHasAvatars || dataHasAvatars,
        }
      })
      .reduce<UsersModelsState>((acc, u) => {
        acc[u.id] = u
        acc[u.token] = u
        if (u.chat_server_uid) acc[u.chat_server_uid] = u
        return acc
      }, {})

    return { ...state, ...models }
  })

const usersMetaSlice = createReduxSlice<{ [key: string]: UserMeta }>('users_meta')

const usersPerksSlice = createReduxSlice<{ [key: string]: string[] }>('users_perks')

/** fetchUser keys its meta by whatever it looked the user up by — id, token or chat uid. */
export const startUserLoadSlice = (key: string) => usersMetaSlice.update(state => ({ ...state, [key]: { loaded: true, loading: true, error: null } }))

export const finishUserLoadSlice = (key: string) =>
  usersMetaSlice.update(state => ({ ...state, [key]: { ...(state?.[key] ?? DEFAULT_USER_META), loading: false, error: null } }))

export const failUserLoadSlice = (key: string, error: Error) =>
  usersMetaSlice.update(state => ({ ...state, [key]: { ...(state?.[key] ?? DEFAULT_USER_META), loading: false, error: excludeAbortError(error) } }))

export const setUserPerksSlice = (user_id: string, perkIds: string[]) => usersPerksSlice.update(state => ({ ...state, [user_id]: perkIds }))

export const setUsersModelsSlice = (models: UsersModelsState) => usersModelsSlice.set(models)

/** The favourite flag lives on the user, so a subscription change writes it under every key. */
export const setUserFavoriteSlice = (userId: string, favorite: boolean) =>
  usersModelsSlice.update(state => {
    const model = state?.[userId]
    if (!model) return state
    const next = { ...model, favorite }
    return {
      ...state,
      [next.id]: next,
      [next.token]: next,
      ...(next.chat_server_uid ? { [next.chat_server_uid]: next } : {}),
    }
  })

/** A list read that carried whole users: nothing more needs fetching for those. */
export const markUsersLoadedSlice = (keys: string[]) =>
  usersMetaSlice.update(state => ({
    ...state,
    ...keys.reduce<{ [key: string]: UserMeta }>((acc, key) => {
      acc[key] = { loaded: true, loading: false, error: null }
      return acc
    }, {}),
  }))

export const applyFirebasePhonesSlice = (batch: FirebaseBatch) =>
  usersModelsSlice.update(state => {
    const idPhoneMap = Object.values(batch.requests.models).reduce<{ [key: string]: string }>((c, r) => {
      if (r.attributes.parent.phone) {
        c[r.attributes.parent.id] = r.attributes.parent.phone
      }
      if (r.attributes.sitter.phone) {
        c[r.attributes.sitter.id] = r.attributes.sitter.phone
      }
      return c
    }, {})

    const pState: UsersModelsState = {}

    for (const [id, phone] of Object.entries(idPhoneMap)) {
      const user = state?.[id]
      if (!user) continue
      if (user.phone !== phone) {
        const model = { ...user, phone }
        pState[user.id] = model
        pState[user.token] = model
        if (user.chat_server_uid) pState[user.chat_server_uid] = model
      }
    }

    if (Object.keys(pState).length) {
      return { ...state, ...pState }
    }

    return state
  })

export const usersModelsSelector = createSelector([usersModelsSlice.selector], models => models ?? EMPTY_MODELS)
export const usersMetaSelector = createSelector([usersMetaSlice.selector], meta => meta ?? EMPTY_META)
export const usersPerksSelector = createSelector([usersPerksSlice.selector], perks => perks ?? EMPTY_PERKS)

export const createUserMetaSelector = (key: string) => createSelector([usersMetaSelector], meta => meta[key] ?? DEFAULT_USER_META)

export interface UserMeta {
  loaded: boolean
  loading: boolean
  error: Error | null
}

const DEFAULT_USER_META: UserMeta = {
  loaded: false,
  loading: false,
  error: null,
}

const EMPTY_MODELS: UsersModelsState = {}

const EMPTY_META: { [key: string]: UserMeta } = {}

const EMPTY_PERKS: { [key: string]: string[] } = {}
